/**
 * HM-083 — Keyboard shortcuts hook reception.
 *
 * Mapping (Fase 1):
 *   "/"   focus search (CustomEvent `hm:focus-search` ascoltato dalla
 *         SearchBar)
 *   "r"   refetch rooms + bookings (queryClient.invalidateQueries)
 *   "?"   toggle help overlay
 *   "Esc" close help overlay
 *   "c"/"o" placeholder per future "selezione camera + check-in/out
 *         rapido" (oggi richiedono context selezione che ancora non
 *         abbiamo, mostriamo l'help inline come hint).
 *
 * Suppressi quando il focus e' su input/textarea/select (per non
 * interferire con la digitazione), tranne `Escape` che resta sempre
 * attivo (utile a uscire da una modal).
 */
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

export type ShortcutHandlers = {
  onToggleHelp: () => void;
  onCloseHelp: () => void;
  /** HM-131 cluster 13: opzionali, se forniti i tasti c/o richiamano
   * l'azione contestuale Dashboard invece del fallback help. */
  onCheckin?: () => void;
  onCheckout?: () => void;
};

export const FOCUS_SEARCH_EVENT = 'hm:focus-search';

function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function useKeyboardShortcuts(handlers: ShortcutHandlers): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Esc sempre attivo (chiusura overlay).
      if (e.key === 'Escape') {
        handlers.onCloseHelp();
        return;
      }
      // Bloccati durante typing.
      if (isTypingTarget(e.target)) return;
      // Ignora combinazioni con modificatori (lasciamo le shortcut OS).
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case '/':
          e.preventDefault();
          window.dispatchEvent(new CustomEvent(FOCUS_SEARCH_EVENT));
          break;
        case 'r':
          e.preventDefault();
          refetchAll(queryClient);
          break;
        case '?':
          e.preventDefault();
          handlers.onToggleHelp();
          break;
        // c / o: azione contestuale (HM-131 cluster 13) sulla tile
        // attualmente focused; fallback help se nessuna tile valida.
        case 'c':
          e.preventDefault();
          if (handlers.onCheckin) handlers.onCheckin();
          else handlers.onToggleHelp();
          break;
        case 'o':
          e.preventDefault();
          if (handlers.onCheckout) handlers.onCheckout();
          else handlers.onToggleHelp();
          break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handlers, queryClient]);
}

function refetchAll(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['rooms'] });
  void qc.invalidateQueries({ queryKey: ['bookings'] });
}
